// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import KeyIcon from '@mui/icons-material/VpnKey';
import { useTheme } from '../context/ThemeContext';
import axios from '../utils/axiosConfig';

const SecretThemeConfig = ({ open, onClose }) => {
  const { isDarkMode, setIsDarkMode } = useTheme();
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [tempThemeMode, setTempThemeMode] = useState(isDarkMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // PIN change state
  const [changePinMode, setChangePinMode] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset state when dialog opens
      setPin('');
      setShowPin(false);
      setTempThemeMode(isDarkMode);
      setError('');
      setSuccess('');
      setChangePinMode(false);
      setCurrentPin('');
      setNewPin('');
      setConfirmNewPin('');
    }
  }, [open, isDarkMode]);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post('/theme/update', {
        is_dark_mode: tempThemeMode,
        pin: pin,
      });

      if (response.data) {
        setSuccess('Theme updated successfully!');
        setIsDarkMode(tempThemeMode);

        // Close dialog after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid PIN. Please try again.');
      } else {
        setError('Failed to update theme. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePinSubmit = async () => {
    if (newPin !== confirmNewPin) {
      setError('New PINs do not match');
      return;
    }

    if (newPin.length < 4) {
      setError('PIN must be at least 4 characters');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post('/theme/update-pin', {
        current_pin: currentPin,
        new_pin: newPin,
      });

      if (response.data) {
        setSuccess('PIN changed successfully!');
        setCurrentPin('');
        setNewPin('');
        setConfirmNewPin('');
        setChangePinMode(false);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid current PIN');
      } else if (err.response?.status === 403) {
        setError('Only administrators can change the PIN');
      } else {
        setError('Failed to change PIN. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyIcon color="primary" />
        Theme Configuration
      </DialogTitle>

      <DialogContent dividers>
        {!changePinMode ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter PIN to modify theme settings
              </Typography>

              <TextField
                fullWidth
                label="Configuration PIN"
                type={showPin ? 'text' : 'password'}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPin(!showPin)} edge="end">
                        {showPin ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && pin) {
                    handleSubmit();
                  }
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                p: 2,
                backgroundColor: tempThemeMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Theme Mode
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={!tempThemeMode}
                    onChange={(e) => setTempThemeMode(!e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tempThemeMode ? (
                      <>
                        <DarkModeIcon fontSize="small" />
                        <Typography>Dark Mode</Typography>
                      </>
                    ) : (
                      <>
                        <LightModeIcon fontSize="small" />
                        <Typography>Light Mode</Typography>
                      </>
                    )}
                  </Box>
                }
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => setChangePinMode(true)}
                sx={{ textTransform: 'none' }}
              >
                Change PIN
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Change Configuration PIN
            </Typography>

            <TextField
              fullWidth
              label="Current PIN"
              type={showCurrentPin ? 'text' : 'password'}
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPin(!showCurrentPin)} edge="end">
                      {showCurrentPin ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New PIN"
              type={showNewPin ? 'text' : 'password'}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPin(!showNewPin)} edge="end">
                      {showNewPin ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New PIN"
              type={showNewPin ? 'text' : 'password'}
              value={confirmNewPin}
              onChange={(e) => setConfirmNewPin(e.target.value)}
              sx={{ mb: 2 }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {!changePinMode ? (
          <Button onClick={handleSubmit} variant="contained" disabled={!pin || loading}>
            Apply Theme
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setChangePinMode(false);
                setError('');
                setCurrentPin('');
                setNewPin('');
                setConfirmNewPin('');
              }}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              onClick={handleChangePinSubmit}
              variant="contained"
              disabled={!currentPin || !newPin || !confirmNewPin || loading}
            >
              Change PIN
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SecretThemeConfig;
